/**
 * Plan payment via Razorpay
 */
import { ApiServices } from './apiServices';
import { ApiConfig } from './ApiConfigs';
import { openRazorpayCheckout } from '../utils/razorpayCheckout';

export const getPaymentConfig = async () => {
  const res = await ApiServices('get', ApiConfig.PLANS_PAYMENT_CONFIG);
  if (res?.success && res.data && typeof res.data.enabled === 'boolean') {
    return res.data;
  }
  return { enabled: false, keyId: '' };
};

/** Whether Razorpay checkout should run for a paid plan */
export const isPaymentRequired = async (planPrice) => {
  const price = parseFloat(planPrice) || 0;
  if (price <= 0) return false;
  const config = await getPaymentConfig();
  return !!(config.enabled && config.keyId);
};

export const createPaymentOrder = async (payload) => {
  const res = await ApiServices('post', ApiConfig.PLANS_PAYMENT_CREATE_ORDER, payload);
  return res;
};

export const verifyPayment = async (payload) => {
  const res = await ApiServices('post', ApiConfig.PLANS_PAYMENT_VERIFY, payload);
  return res;
};

/**
 * Full checkout: create order → Razorpay modal → verify → activate plan
 */
export const purchasePlanWithRazorpay = async ({
  storeId,
  planId,
  endDate = null,
  planName = 'Plan',
  planPrice,
  user,
  type = 'assign',
  chargeId = null,
}) => {
  const orderRes = await createPaymentOrder({
    storeId,
    planId,
    endDate,
    type,
    chargeId,
  });

  if (!orderRes?.success) {
    throw new Error(orderRes?.message || 'Failed to create payment order');
  }

  if (orderRes.data?.freePlan) {
    const { assignPlanToStore } = await import('./planService');
    return assignPlanToStore({ storeId, planId, endDate });
  }

  const { orderId, amount, currency, keyId } = orderRes.data;
  if (!orderId || !keyId) {
    throw new Error('Invalid payment order response');
  }

  const paymentResponse = await openRazorpayCheckout({
    keyId,
    orderId,
    amount,
    currency: currency || 'INR',
    name: 'Driver App',
    description: planName,
    prefill: {
      name: user?.firstName
        ? `${user.firstName} ${user.lastName || ''}`.trim()
        : user?.name,
      email: user?.email,
      contact: user?.phoneNumber,
    },
  });

  const verifyRes = await verifyPayment({
    razorpay_order_id: paymentResponse.razorpay_order_id,
    razorpay_payment_id: paymentResponse.razorpay_payment_id,
    razorpay_signature: paymentResponse.razorpay_signature,
    storeId,
    planId,
    endDate,
    type,
    chargeId,
  });

  if (!verifyRes?.success) {
    throw new Error(verifyRes?.message || 'Payment verification failed');
  }

  return verifyRes;
};

export default {
  getPaymentConfig,
  isPaymentRequired,
  createPaymentOrder,
  verifyPayment,
  purchasePlanWithRazorpay,
};
